import { format } from "date-fns"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { Sun, Clock, Calendar, MapPin, FileText } from "lucide-react"
import { formatDbTimestampToLocal } from "@/lib/utils/date"
import { useMediaQuery } from "@/hooks/use-media-query"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface NapRecordsTableProps {
  childId: string
  limit?: number
  timezone?: string
}

export function NapRecordsTable({ childId, limit, timezone }: NapRecordsTableProps) {
  const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneAbbr = userTimezone ? 
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: userTimezone })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value || userTimezone
    : 'Local';
  
  const isSmall = useMediaQuery("(max-width: 640px)");
  const isMobile = useMediaQuery("(max-width: 479px)");

  const { data: napRecords, isLoading } = useQuery({
    queryKey: ['napRecords', childId, limit],
    queryFn: async () => {
      let query = supabase
        .from('nap_sessions')
        .select(`
          *,
          nap_location(description),
          sleep_environment(description),
          sleep_onset_method(description),
          restfulness_rating(description)
        `)
        .eq('child_id', childId)
        .order('start_time', { ascending: false })

      if (limit) {
        query = query.limit(limit)
      }

      const { data, error } = await query

      if (error) throw error
      return data
    },
    enabled: !!childId
  })

  if (isLoading) {
    return (
      <div className="p-4 text-center text-purple-300/70 animate-pulse">
        Loading nap records...
      </div>
    )
  }

  if (!napRecords?.length) {
    return (
      <div className="p-4 text-center text-gray-400">
        No nap records found
      </div>
    )
  }

  return (
    <div className="rounded-xl overflow-hidden border border-purple-500/20 backdrop-blur-xl bg-purple-500/5">
      <div className="p-3 sm:p-4 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-b border-purple-500/20">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Nap Records</h3>
          <div className="text-xs text-gray-400">
            All times shown in {timezoneAbbr}
          </div>
        </div>
      </div>

      <div className="p-2 sm:p-4 overflow-x-auto">
        <div className="rounded-lg overflow-hidden min-w-[600px] sm:min-w-0">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-purple-500/20 hover:bg-transparent">
                <TableHead className="text-xs sm:text-sm text-purple-300 py-2 sm:py-3.5">Start Date</TableHead>
                <TableHead className="text-xs sm:text-sm text-purple-300 py-2 sm:py-3.5">End Date</TableHead>
                <TableHead className="text-xs sm:text-sm text-purple-300 py-2 sm:py-3.5">Nap Window</TableHead>
                <TableHead className="text-xs sm:text-sm text-purple-300 py-2 sm:py-3.5">Duration</TableHead>
                <TableHead className="text-xs sm:text-sm text-purple-300 py-2 sm:py-3.5">Quality</TableHead>
                {!isSmall && (
                  <TableHead className="text-xs sm:text-sm text-purple-300 py-2 sm:py-3.5">
                    <div className="flex items-center space-x-1">
                      <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>Notes</span>
                    </div>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence>
                {napRecords.map((record, index) => {
                  const duration = new Date(record.end_time).getTime() - new Date(record.start_time).getTime()
                  const hours = Math.floor(duration / (1000 * 60 * 60))
                  const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60))
                  
                  // Check if notes exist and are not empty
                  const hasNotes = record.notes && record.notes.trim().length > 0;
                  
                  return (
                    <motion.tr 
                      key={record.id} 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={cn(
                        "border-b border-purple-500/10",
                        "transition-colors duration-200",
                        "hover:bg-purple-500/5"
                      )}
                    >
                      <TableCell className="text-xs sm:text-sm text-gray-300 py-2 sm:py-3">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
                          <span className="text-xs sm:text-sm">
                            {formatDbTimestampToLocal(record.start_time, "MMM DD, YYYY")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm text-gray-300 py-2 sm:py-3">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
                          <span className="text-xs sm:text-sm">
                            {formatDbTimestampToLocal(record.end_time, "MMM DD, YYYY")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm text-gray-300 py-2 sm:py-3">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                          <span className="text-xs sm:text-sm">
                            {formatDbTimestampToLocal(record.start_time, "h:mm a")} - 
                            {formatDbTimestampToLocal(record.end_time, "h:mm a")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm text-gray-300 py-2 sm:py-3">
                        <div className="flex items-center gap-1.5">
                          <Sun className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                          {hours > 0 ? `${hours}h ` : ''}{minutes}m
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm py-2 sm:py-3">
                        <span className="inline-flex items-center rounded-full px-1.5 sm:px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-400 gap-1">
                          <Sun className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          {record.restfulness_rating?.description || 'N/A'}
                        </span>
                      </TableCell>
                      {!isSmall && (
                        <TableCell className="text-xs sm:text-sm py-2 sm:py-3">
                          {hasNotes ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center cursor-help">
                                    <FileText className="w-3.5 h-3.5 text-purple-400 mr-1" />
                                    <span className="text-xs text-gray-300 truncate max-w-[100px]">
                                      {record.notes.substring(0, 15)}{record.notes.length > 15 ? '...' : ''}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[300px] text-xs">
                                  <p>{record.notes}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-xs text-gray-500">No notes</span>
                          )}
                        </TableCell>
                      )}
                    </motion.tr>
                  )
                })}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
